import React, { useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../../common/Button';
import { cn } from '../../../utils/cn';
import { validateAll } from './validation';
import type { ImportRow } from './types';
import { NumberCell, RupiahCell, RoastTypeCell, SourceGreenBeanCell } from './cells';

interface ReviewStepProps {
  initialRows: ImportRow[];
  onBack: () => void;
  onSubmit: (rows: ImportRow[]) => Promise<void>;
}

interface ColumnDef {
  key: keyof ImportRow;
  label: string;
  required: boolean;
  width: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'sourceGreenBeanLabel', label: 'Source Green Bean', required: true, width: 'w-72' },
  { key: 'initialQty',           label: 'Stock (kg)',        required: true, width: 'w-32' },
  { key: 'standardCost',         label: 'Standard Cost',     required: true, width: 'w-32' },
  { key: 'roastType',            label: 'Roast Type',        required: true, width: 'w-40' },
];

export const ReviewStep: React.FC<ReviewStepProps> = ({ initialRows, onBack, onSubmit }) => {
  const [rows, setRows] = useState<ImportRow[]>(initialRows);
  const [included, setIncluded] = useState<boolean[]>(() => initialRows.map(() => true));
  const [busy, setBusy] = useState(false);

  const errors = useMemo(
    () => validateAll(rows).filter((e) => included[e.rowIndex]),
    [rows, included],
  );

  const errorByCell = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of errors) m.set(`${e.rowIndex}:${e.field}`, e.message);
    return m;
  }, [errors]);

  const errorRowSet = useMemo(() => new Set(errors.map((e) => e.rowIndex)), [errors]);
  const includedCount = included.filter(Boolean).length;
  const errorRowCount = errorRowSet.size;
  const canSubmit = !busy && includedCount > 0 && errorRowCount === 0;

  const update = <K extends keyof ImportRow>(rowIdx: number, field: K, value: ImportRow[K]) => {
    setRows((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, [field]: value } : r)));
  };

  const toggleIncluded = (rowIdx: number) => {
    setIncluded((prev) => prev.map((v, i) => (i === rowIdx ? !v : v)));
  };

  const allIncluded = included.length > 0 && included.every(Boolean);
  const noneIncluded = !included.some(Boolean);
  const toggleAll = () => {
    const next = !allIncluded;
    setIncluded((prev) => prev.map(() => next));
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await onSubmit(rows.filter((_, i) => included[i]));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-600">
          Review and edit the rows below. Source Green Bean is read-only — to change it,
          fix the row in Excel and re-upload.
        </p>
        <div className="flex items-center gap-2 text-xs">
          <span
            data-test-id="import-rb-included-count"
            className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 font-medium text-gray-700"
          >
            <CheckCircle2 className="h-3.5 w-3.5 text-gray-500" />
            {includedCount} included
          </span>
          <span
            data-test-id="import-rb-error-count"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium',
              errorRowCount === 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800',
            )}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {errorRowCount} {errorRowCount === 1 ? 'row needs attention' : 'rows need attention'}
          </span>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="overflow-auto max-h-[60vh]">
          <table className="min-w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_rgb(229_231_235)]">
              <tr>
                <th className="w-10 px-3 py-2.5 text-left">
                  <input
                    type="checkbox"
                    checked={allIncluded}
                    ref={(el) => {
                      if (el) el.indeterminate = !allIncluded && !noneIncluded;
                    }}
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    data-test-id="import-rb-toggle-all"
                  />
                </th>
                <th className="w-10 px-1 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  #
                </th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={cn(
                      'px-2 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap',
                      c.width,
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.required && <span className="text-red-500">*</span>}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, rowIdx) => {
                const isIncluded = included[rowIdx];
                const rowHasError = errorRowSet.has(rowIdx) && isIncluded;
                return (
                  <tr
                    key={rowIdx}
                    className={cn(
                      'transition-colors',
                      !isIncluded && 'bg-gray-50/60 text-gray-400',
                      isIncluded && rowHasError && 'bg-red-50/30',
                      isIncluded && !rowHasError && 'hover:bg-gray-50/60',
                    )}
                  >
                    <td className="px-3 py-1.5 align-middle">
                      <input
                        data-test-id={`import-rb-row-checkbox-${rowIdx}`}
                        type="checkbox"
                        checked={isIncluded}
                        onChange={() => toggleIncluded(rowIdx)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-1 py-1.5 align-middle text-xs tabular-nums text-gray-400">
                      {rowIdx + 1}
                    </td>
                    {COLUMNS.map((col) => {
                      // sourceGreenBeanLabel cell shows display text, but errors
                      // are keyed by the underlying sourceGreenBeanItemId field.
                      const errKey = `${rowIdx}:${
                        col.key === 'sourceGreenBeanLabel' ? 'sourceGreenBeanItemId' : col.key
                      }`;
                      const err = isIncluded ? errorByCell.get(errKey) : undefined;
                      const cellTestId = `import-rb-cell-${rowIdx}-${col.key}`;
                      return (
                        <td key={col.key} className="px-1 py-1 align-middle">
                          {col.key === 'sourceGreenBeanLabel' ? (
                            <SourceGreenBeanCell
                              label={row.sourceGreenBeanLabel}
                              hasError={!!err}
                              errorMessage={err}
                              testId={cellTestId}
                            />
                          ) : col.key === 'roastType' ? (
                            <RoastTypeCell
                              value={row.roastType}
                              onChange={(v) => update(rowIdx, 'roastType', v)}
                              hasError={!!err}
                              errorMessage={err}
                              testId={cellTestId}
                            />
                          ) : col.key === 'standardCost' ? (
                            <RupiahCell
                              value={row.standardCost}
                              onChange={(v) => update(rowIdx, 'standardCost', v)}
                              hasError={!!err}
                              errorMessage={err}
                              testId={cellTestId}
                            />
                          ) : (
                            <NumberCell
                              value={row[col.key as 'initialQty']}
                              onChange={(v) => update(rowIdx, col.key as 'initialQty', v)}
                              hasError={!!err}
                              errorMessage={err}
                              testId={cellTestId}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
        <Button
          variant="ghost"
          onClick={onBack}
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          data-test-id="import-rb-back"
        >
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit}
          isLoading={busy}
          onClick={handleSubmit}
          data-test-id="import-rb-submit"
        >
          Submit ({includedCount})
        </Button>
      </div>
    </div>
  );
};
